import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OrderResponse {
  message: string;
  payment_link?: string;
  paymentLink?: string; // New API field
  session_id?: string;
  [key: string]: any; // Allow additional fields from API response
}

export interface PaymentStatusRequest {
  order_id: string;
  restaurant_id: string;
}

export interface PaymentStatusResponse {
  message: string;
  status: boolean;
  order_id?: string;
  payment_status?: string;
  order_details?: any;
  created_at?: string;
  updated_at?: string;
}

// V2 API Interfaces
export interface ModifierOption {
  id: string;
  restaurantId?: string;
  name: string;
  price: string;
  image: string;
  isEnabled: boolean;
  childGroupId: string;
  posReferenceId: string;
  modifier?: ModifierGroup;
}

export interface ModifierGroup {
  id: string;
  restaurantId?: string;
  posReferenceId: string;
  name: string;
  isRequired: boolean;
  allowMultiples: boolean;
  minRequired: number;
  maxAllowed: number;
  sortOrder: number;
  options: ModifierOption[];
}

export interface LineItem {
  id: string;
  name: string;
  posReferenceId: string;
  quantity: number;
  price: number;
  image: string;
  modifier: ModifierGroup[];
}

export interface OrderRequestV2 {
  customer: {
    name: string;
    phone: string;
    email: string;
    address: {
      address: string;
      city: string;
      state: string;
      country: string;
      zipcode: number;
    };
  };
  source: 'web';
  fulfillmentType: 'pickup' | 'delivery';
  notes: string;
  paymentMode: 'pay_now' | 'pay_later';
  lineItems: LineItem[];
  callId?: string;
}

interface OrderState {
  loading: boolean;
  orderResponse: OrderResponse | null;
  error: string | null;
  paymentStatusLoading: boolean;
  paymentStatusResponse: PaymentStatusResponse | null;
  paymentStatusError: string | null;
}

const initialState: OrderState = {
  loading: false,
  orderResponse: null,
  error: null,
  paymentStatusLoading: false,
  paymentStatusResponse: null,
  paymentStatusError: null,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    fetchPaymentStatusRequest: (state, action: PayloadAction<PaymentStatusRequest>) => {
      state.paymentStatusLoading = true;
      state.paymentStatusError = null;
      state.paymentStatusResponse = null;
    },
    
    fetchPaymentStatusSuccess: (state, action: PayloadAction<PaymentStatusResponse>) => {
      state.paymentStatusLoading = false;
      state.paymentStatusResponse = action.payload;
      state.paymentStatusError = null;
    },
    
    fetchPaymentStatusFailure: (state, action: PayloadAction<string>) => {
      state.paymentStatusLoading = false;
      state.paymentStatusError = action.payload;
      state.paymentStatusResponse = null;
    },
    
    clearPaymentStatusState: (state) => {
      state.paymentStatusLoading = false;
      state.paymentStatusResponse = null;
      state.paymentStatusError = null;
    },
    
    // V2 API Actions
    orderRequest: (state, action: PayloadAction<OrderRequestV2>) => {
      state.loading = true;
      state.error = null;
      state.orderResponse = null;
    },
    
    orderSuccess: (state, action: PayloadAction<OrderResponse>) => {
      state.loading = false;
      state.orderResponse = action.payload;
      state.error = null;
    },
    
    orderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.orderResponse = null;
    },
  },
});

export const {
  fetchPaymentStatusRequest,
  fetchPaymentStatusSuccess,
  fetchPaymentStatusFailure,
  clearPaymentStatusState,
  orderRequest,
  orderSuccess,
  orderFailure,
} = orderSlice.actions;

export default orderSlice.reducer;
